import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getDatewiseFaultCountByTC,
  getHealthStatusByLocationOrAsset,
} from "src/core/api/services/dashboard.services";
import DashboardTrandingChart from "../custom-trending-chart/DashboardTrandingChart";
import "./faultInfoBox.scss";
import FaultTypeInfoBoxes from "./FaultTypeInfoBoxes";
import { Spinner } from "react-bootstrap";
import { B2Row, B2Col, B2Card } from "@hdb/biome-2";
import * as moment from "moment";

const FaultInfoBox = ({ className }: any) => {
  const [faultInfo, setFaultInfo] = useState({
    totalFaults: 0,
    lightFailure: 0,
    sensorFailure: 0,
    networkFailure: 0,
  });

  const [faultChartData, setFaultChartData] = useState<any>({
    xAxisData: [],
    seriesData: [],
    extraData: [],
  });

  const [isAllZero, setIsAllZero] = useState<boolean>(false);

  const selectedTcCode = useSelector(
    (state: any) => state.dashboard.selectedTcCode
  );

  const {
    data: healthStatusByLocationOrAsset,
    isLoading: isLoadingHealthStatusByLocationOrAsset,
  } = useQuery({
    queryKey: ["healthStatusByLocationOrAsset", selectedTcCode],
    queryFn: ({ signal }) =>
      getHealthStatusByLocationOrAsset(selectedTcCode, signal),
    enabled: !!selectedTcCode,
  });

  const { data: datewiseFaultCount, isLoading: isLoadingDatewiseFaultCount } =
    useQuery({
      queryKey: ["datewiseFaultCount", selectedTcCode],
      queryFn: ({ signal }) =>
        getDatewiseFaultCountByTC(selectedTcCode, signal),
      enabled: !!selectedTcCode,
    });

  useEffect(() => {
    if (datewiseFaultCount?.length > 0) {
      const xData = datewiseFaultCount?.map((d: any) => d?.faultDate);

      const series = datewiseFaultCount?.map((d: any) => d?.reportedFaultCount);
      // const series = datewiseFaultCount?.map((d: any) => 0);

      const acknowledgedFaults = datewiseFaultCount?.map(
        (d: any) => d?.acknowledgedFaultCount
      );
      // const acknowledgedFaults = datewiseFaultCount?.map((d: any) => 0);

      const resolvedFaults = datewiseFaultCount?.map(
        (d: any) => d?.resolvedFaultCount
      );
      // const resolvedFaults = datewiseFaultCount?.map((d: any) => 0);

      const extraData = [acknowledgedFaults, resolvedFaults];

      // check every value of series is equal to 0
      const isAllZeroValue = series.every((val: any) => val === 0);
      setIsAllZero(isAllZeroValue);

      setFaultChartData({
        xAxisData: xData,
        seriesData: series,
        extraData: extraData,
      });
    }
  }, [datewiseFaultCount]);

  useEffect(() => {
    if (
      !healthStatusByLocationOrAsset ||
      healthStatusByLocationOrAsset?.length === 0 ||
      !healthStatusByLocationOrAsset[0]
    )
      return;

    const { lightFailure, sensorFailure, networkFailure } =
      healthStatusByLocationOrAsset[0];

    const totalFaults = lightFailure + sensorFailure + networkFailure;

    setFaultInfo({
      totalFaults: totalFaults,
      lightFailure: lightFailure,
      sensorFailure: sensorFailure,
      networkFailure: networkFailure,
    });
  }, [healthStatusByLocationOrAsset]);

  const navigateToFault = (faultType: string, startDate?: string) => {
    const url = new URL(`${window.location.origin}/fault`);
    url.searchParams.append("tc", selectedTcCode);
    url.searchParams.append("faultType", faultType);
    url.searchParams.append("isAcknowledged", "2");

    if (startDate) {
      url.searchParams.append("startDate", startDate);
    }

    window.open(url.toString(), "_blank");
  };

  const handleChartsPointClick = (xValue: string) => {
    const months = [];
    const currentDate = moment();

    for (let i = 0; i < 6; i++) {
      const date = currentDate.clone().subtract(i, "months");
      months.push({
        abbr: date.format("MMM"),
        yearMonth: date.format("YYYY-MM"),
      });
    }

    const yearMonth = months.find((month) => month.abbr === xValue)?.yearMonth;

    navigateToFault("0", yearMonth);
  };

  return (
    <B2Col xs={12} lg={6} className="mb-3 mb-lg-0 px-lg-2">
      <B2Card className="p-3">
        <B2Row className="d-flex align-items-center">
          <h4 className="fw-bold mb-3 textNeutral600 d-block d-sm-none">
            Faults
          </h4>

          <B2Col xs={12} md={6}>
            <h4 className="textNeutral600 fw-bold mb-3 d-none d-sm-block">
              Faults
            </h4>
            <div className="d-flex justify-content-between align-items-center">
              <FaultTypeInfoBoxes
                count={faultInfo?.totalFaults}
                // count={0}
                isloading={isLoadingHealthStatusByLocationOrAsset}
                name="Faults"
                type="total"
                onClick={() => navigateToFault("0")}
              />
              <FaultTypeInfoBoxes
                count={faultInfo?.lightFailure}
                // count={0}
                isloading={isLoadingHealthStatusByLocationOrAsset}
                name="Lights"
                onClick={() => navigateToFault("1")}
              />
              <FaultTypeInfoBoxes
                count={faultInfo?.sensorFailure}
                // count={0}
                isloading={isLoadingHealthStatusByLocationOrAsset}
                name="Sensors"
                onClick={() => navigateToFault("2")}
              />
              <FaultTypeInfoBoxes
                count={faultInfo?.networkFailure}
                // count={0}
                isloading={isLoadingHealthStatusByLocationOrAsset}
                name="Networks"
                onClick={() => navigateToFault("4")}
              />
            </div>
          </B2Col>

          <B2Col xs={12} md={6} className="pt-3">
            {isLoadingDatewiseFaultCount ? (
              <div className="textNeutral600 d-flex justify-content-center">
                <Spinner animation="grow" variant="primary" />
              </div>
            ) : (
              <DashboardTrandingChart
                xAxisCategory={faultChartData?.xAxisData}
                seriesData={faultChartData?.seriesData}
                extraData={faultChartData?.extraData}
                extraTitle={["Acknowledged Fault", "Resolved Fault"]}
                xToolTip="Month"
                yToolTip="Reported Fault"
                isShowLevel={true}
                isAllZero={isAllZero}
                onPointClick={handleChartsPointClick}
              />
            )}
            {/* <div className="last30Days">Last 30 days</div> */}
          </B2Col>
        </B2Row>
      </B2Card>
    </B2Col>
  );
};

export default FaultInfoBox;
